import Button from '@/components/ui/Button'
import { Card, CardContent } from '@/components/ui/Card'
import { OTPInput } from '@/components/ui/OTPInput'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
type UiMessage = { title: string; description: string; severity: 'success' | 'warning' | 'error' }

interface OTPFormProps {
  otp: string
  onOtpChange: (value: string) => void
  onSubmit: () => void
  onResend: () => void
  onBack: () => void
  isLoading: boolean
  isResending: boolean
  error: string | null
  notice: UiMessage | null
  cooldown: number
  email: string
}

export function OTPForm({
  otp,
  onOtpChange,
  onSubmit,
  onResend,
  onBack,
  isLoading,
  isResending,
  error,
  notice,
  cooldown,
  email,
}: OTPFormProps) {
  const isComplete = otp.length === 6

  return (
    <div className='w-full max-w-md space-y-8'>
      {/* Header */}
      <div className='text-center space-y-2'>
        <h1 className='headline text-3xl text-foreground'>Check your email</h1>
        <p className='body-copy text-muted-foreground'>
          We've sent a 6-digit verification code to{' '}
          <span className='text-foreground font-medium'>{email}</span>
        </p>
        <button
          onClick={onBack}
          className='text-sm text-primary hover:underline'
        >
          Change email
        </button>
      </div>

      {/* OTP Input Card */}
      <Card>
        <CardContent className='p-6 space-y-6'>
          {/* Status Message */}
          {(notice || error) && (
            <div
              className={
                `rounded-lg p-3 border ` +
                (notice?.severity === 'success'
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : notice?.severity === 'warning'
                  ? 'bg-yellow-50 border-yellow-200 text-yellow-700'
                  : 'bg-destructive/10 border-destructive/30 text-destructive')
              }
            >
              <p className='text-sm font-medium'>
                {notice?.title || 'Verification Issue'}
              </p>
              <p className='text-xs mt-1'>{notice?.description || error}</p>
            </div>
          )}

          <div className='space-y-4'>
            <label className='block text-sm font-medium text-foreground text-center'>
              Enter verification code
            </label>

            {/* OTP Input */}
            <OTPInput value={otp} onChange={onOtpChange} disabled={isLoading} />
          </div>

          {/* Verify Button */}
          <Button
            onClick={onSubmit}
            disabled={!isComplete || isLoading}
            className='w-full'
          >
            {isLoading ? (
              <>
                <LoadingSpinner className='-ml-1 mr-3 h-5 w-5 text-white' />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>

          {/* Resend Code */}
          <div className='text-center space-y-2'>
            <p className='body-copy text-sm text-muted-foreground'>
              Didn't receive the code?
            </p>
            <button
              onClick={onResend}
              disabled={isResending || cooldown > 0}
              className='body-copy text-sm text-primary hover:text-primary/80 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {isResending
                ? 'Sending...'
                : cooldown > 0
                ? `Resend in ${cooldown}s`
                : 'Resend verification code'}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Support */}
      <div className='text-center'>
        <p className='body-copy text-sm text-muted-foreground mb-2'>
          Having trouble?
        </p>
        <a
          href='mailto:contact@apptlyhealth.com?subject=OTP%20Verification%20Support%20Request'
          className='body-copy text-sm text-primary hover:text-primary/80 font-medium transition-colors'
        >
          Contact support
        </a>
      </div>
    </div>
  )
}
